﻿import {hasFlag, HttpClientError} from "@2d-front-monorepo/core";
import {CustomData, HolderDataResult, InstallationId, IntegraHolderData, ListPermitsResult, ListReservationsResult} from "@2d-front-monorepo/permits-business";
import {permitsStore} from "../contexts/PermitsContext";
import {userStore} from "../contexts/UserContext";
import {logOff, permitsLogErrorToServer} from "../functions";
import {RefObject} from "react";
import {getHttpClient} from "./getHttpClient";

let cachedHolderData: HolderDataResult | undefined = undefined;
let cachedPermitsData: ListPermitsResult | undefined = undefined;
let cachedReservationsData: ListReservationsResult | undefined = undefined;

export const enum ReloadEntities {
	None = 0,
	Holder = 1,
	Permits = 2,
	Reservations = 4,
	All = Holder | Permits | Reservations,
}

function getLoadStatus(holder: HolderDataResult | undefined, permits: ListPermitsResult | undefined, reservations: ListReservationsResult | undefined): ReloadEntities {
	let status = ReloadEntities.None;
	if (holder != null) {
		status |= ReloadEntities.Holder;
	}

	if (permits != null && permits.permits.length > 0) {
		status |= ReloadEntities.Permits;
	}

	if (reservations != null && reservations.reservations.length > 0) {
		status |= ReloadEntities.Reservations;
	}

	return status;
}

/** Current load status derived from the caches — unlike globalConfig.initialStatus (frozen at
 *  boot), this reflects everything loaded since, e.g. right after a fresh sign-in. */
export function currentLoadStatus(): ReloadEntities {
	return getLoadStatus(cachedHolderData, cachedPermitsData, cachedReservationsData);
}

let inFlight: Promise<ReloadEntities> | null = null;
let pendingFlags: ReloadEntities = ReloadEntities.None;
let pendingRef: RefObject<InstallationId> | undefined = undefined;
let nextReloadPromise: Promise<ReloadEntities> | null = null;
let resolveNextReload: ((value: ReloadEntities) => void) | null = null;
let rejectNextReload: ((reason?: unknown) => void) | null = null;

export function reloadPermits(entities: ReloadEntities, ref?: RefObject<InstallationId>): Promise<ReloadEntities> {
	if (entities === ReloadEntities.None) {
		return Promise.resolve(getLoadStatus(cachedHolderData, cachedPermitsData, cachedReservationsData));
	}

	if (inFlight != null) {
		pendingFlags |= entities;
		if (ref != null) pendingRef = ref;
		if (nextReloadPromise == null) {
			nextReloadPromise = new Promise<ReloadEntities>((resolve, reject) => {
				resolveNextReload = resolve;
				rejectNextReload = reject;
			});
		}
		return nextReloadPromise;
	}

	return startReload(entities, ref);
}

function startReload(entities: ReloadEntities, ref?: RefObject<InstallationId>): Promise<ReloadEntities> {
	const promise = doReload(entities, ref);
	inFlight = promise;

	promise.then(
		() => drainPending(null),
		(error) => drainPending(error)
	);

	return promise;
}

function drainPending(error: unknown): void {
	if (pendingFlags === ReloadEntities.None) {
		inFlight = null;
		return;
	}

	const followUpFlags = pendingFlags;
	const followUpRef = pendingRef;
	const resolve = resolveNextReload;
	const reject = rejectNextReload;
	pendingFlags = ReloadEntities.None;
	pendingRef = undefined;
	nextReloadPromise = null;
	resolveNextReload = null;
	rejectNextReload = null;
	inFlight = null;

	if (error != null) {
		// doReload normally swallows errors, but be defensive if that contract ever changes.
		reject?.(error);
		return;
	}

	const followUp = startReload(followUpFlags, followUpRef);
	followUp.then(
		(result) => resolve?.(result),
		(err) => reject?.(err)
	);
}

async function doReload(entities: ReloadEntities, ref?: RefObject<InstallationId>): Promise<ReloadEntities> {
	try {
		const httpClient = getHttpClient(ref?.current);

		const [holderData, permits, reservations] = await Promise.all([
			hasFlag(entities, ReloadEntities.Holder) || cachedHolderData == null ? httpClient.holder.get() : cachedHolderData,
			hasFlag(entities, ReloadEntities.Permits) || cachedPermitsData == null ? httpClient.permits.list() : cachedPermitsData,
			hasFlag(entities, ReloadEntities.Reservations) || cachedReservationsData == null ? httpClient.reservations.list() : cachedReservationsData
		]);

		cachedHolderData = holderData;
		cachedPermitsData = permits;
		cachedReservationsData = reservations;

		const serverData = CustomData.getObject<IntegraHolderData>(holderData.holder, "::integra-server-data");

		if (serverData == null) {
			await logOff();

		} else {
			userStore.patchState(state => {
				state.name = holderData.holder.givenName ?? "";
				state.email = holderData.holder.email;
				state.authStatus = "logged-in";
			});

			if (ref != null) ref.current = holderData.installationId;

			permitsStore.setState({
				installationId: holderData.installationId,
				userState: holderData.userState,
				holder: holderData.holder,
				holderData: serverData,
				drivers: holderData.drivers,
				residencies: holderData.residencies,
				vehicles: holderData.vehicles,
				permits: permits.permits,
				hasArchivedPermits: permits.hasArchived,
				reservations: reservations.reservations,
				hasArchivedReservations: reservations.hasArchived,
				voidReservations: reservations.voids
			});
		}

	} catch (error) {
		if (!(error instanceof HttpClientError && (error.status === 401 || error.status === 403))) {
			permitsLogErrorToServer(error);
		}
	}

	return getLoadStatus(cachedHolderData, cachedPermitsData, cachedReservationsData);
}
